import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import ApprovalDetailModal from '../home/approvals/ApprovalDetailModal';
import { OUTCOME_PENDING, TYPE_BUDGET_CHANGE } from '../home/myviews/queueTypes';

import { DASH, localDate } from './changeRequestFormat';

import { decimal } from 'Helpers/currencyFormat';

import Button from 'CommonComponents/Button';

import useModal from 'Hooks/useModal';

import ChevronLeftIcon from 'Assets/icons/chevron-left.svg';
import ClockIcon from 'Assets/icons/clock.svg';
import XMarkIcon from 'Assets/icons/xmark.svg';

/**
 * Pending budget changes on a department or cost centre's budget summary.
 *
 * The Breakdown table is jQuery DataTables and paginated on the server, so the
 * chips on its rows are drawn by expenditureTable.js from the API this publishes
 * on `window.ibPendingLineChanges`, and redrawn on `pendinglinechanges:changed`.
 * Every count comes from the payload, never from the rows on screen, so a group
 * counts lines that are collapsed, filtered out or on another page.
 *
 * This renders the rest: the toolbar toggle, the summary tile notes, the pop-up a
 * chip opens, and the Home approval pop-up for an approver acting from here.
 */

const POPOVER_WIDTH = 360;
const STORAGE_KEY = 'pending-line-changes-visible';

export const formatChange = (value) => {
    const amount = Number(value ?? 0);

    return (amount < 0 ? '−' : '+') + decimal(Math.abs(amount), 0);
};

const readVisible = () => {
    try {
        return window.localStorage.getItem(STORAGE_KEY) !== '0';
    } catch {
        return true;
    }
};

const storeVisible = (visible) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, visible ? '1' : '0');
    } catch {
        // A blocked store only means the choice is not remembered.
    }
};

function publish(payload, visible) {
    const items = payload?.items ?? [];

    window.ibPendingLineChanges = {
        visible,
        formatChange,
        forLine: (expenseId) => {
            const lines = items.filter((item) => String(item.expense_id) === String(expenseId));

            if (!lines.length) {
                return null;
            }

            return { count: lines.length, change: lines.reduce((sum, item) => sum + item.change, 0), pot: lines[0].pot };
        },
        forGroup: (node, id) => {
            const key = `${node}:${id}`;
            const lines = items.filter((item) => item.groups.includes(key));

            if (!lines.length) {
                return null;
            }

            return {
                count: lines.length,
                cash: lines.filter((item) => item.pot === 'cash').length,
                vik: lines.filter((item) => item.pot === 'vik').length,
            };
        },
    };

    window.dispatchEvent(new CustomEvent('pendinglinechanges:changed'));
}

export default function PendingLineChanges({ endpoint }) {
    const [payload, setPayload] = useState(null);
    const [visible, setVisible] = useState(readVisible);
    const [popover, setPopover] = useState(null);
    const [approvalRow, setApprovalRow] = useState(null);
    const approvalModal = useModal();

    const load = useCallback(() => {
        axios
            .get(endpoint)
            .then(({ data }) => setPayload(data))
            // The figures on the page are unaffected; only the markers are missing.
            .catch(() => setPayload(null));
    }, [endpoint]);

    useEffect(load, [load]);

    // A change sent for approval from the table itself raises a new pending request.
    useEffect(() => {
        window.addEventListener('pendinglinechanges:reload', load);

        return () => window.removeEventListener('pendinglinechanges:reload', load);
    }, [load]);

    useEffect(() => publish(payload, visible), [payload, visible]);

    useEffect(() => {
        approvalRow ? approvalModal.show() : approvalModal.hide();
    }, [approvalRow]);

    const items = payload?.items ?? [];

    const open = useCallback(
        (anchor, matching) => {
            if (!matching.length) {
                return;
            }

            setPopover({ anchor, list: matching.length > 1 ? matching : null, selected: matching.length > 1 ? null : matching[0] });
        },
        [setPopover],
    );

    // Chips in the table are jQuery markup, so they are reached by delegation.
    useEffect(() => {
        const onClick = (event) => {
            const chip = event.target.closest('.bc-pending-chip[data-pending-line], .bc-pending-chip[data-pending-node]');

            if (!chip) {
                return;
            }

            event.preventDefault();

            if (chip.dataset.pendingLine !== undefined) {
                open(
                    chip,
                    items.filter((item) => String(item.expense_id) === chip.dataset.pendingLine),
                );
            } else {
                const key = `${chip.dataset.pendingNode}:${chip.dataset.pendingNodeId}`;
                const matching = items.filter((item) => item.groups.includes(key));

                setPopover(matching.length ? { anchor: chip, list: matching, selected: null } : null);
            }
        };

        document.addEventListener('click', onClick);

        return () => document.removeEventListener('click', onClick);
    }, [items, open]);

    const toggleVisible = () => {
        storeVisible(!visible);
        setVisible(!visible);
        setPopover(null);
    };

    const afterAction = () => {
        setApprovalRow(null);
        setPopover(null);
        load();

        if (typeof window.triggerDataTables2Request === 'function') {
            window.triggerDataTables2Request();
        }
    };

    const startApproval = (item) => {
        setPopover(null);
        setApprovalRow({
            type: TYPE_BUDGET_CHANGE,
            type_label: $t('Budget change'),
            title: item.line_name,
            record: { id: item.request_id },
            outcome: OUTCOME_PENDING,
            can_act_inline: true,
            href: item.budget_changes_url,
        });
    };

    const count = payload?.count ?? 0;
    const toggleRoot = document.getElementById('pending-line-changes-toggle');
    const legendRoot = document.getElementById('pending-line-changes-legend');
    const tileRoots = Array.from(document.querySelectorAll('[data-pending-line-changes-tile]'));

    return (
        <>
            {toggleRoot &&
                count > 0 &&
                ReactDOM.createPortal(
                    <button
                        type="button"
                        aria-pressed={visible}
                        title={$t('Pending changes ({{count}})', { count })}
                        onClick={toggleVisible}
                        className={`expenditure-toolbar__control expenditure-toolbar__button${
                            visible ? ' !tw-border-amber-700 !tw-bg-amber-100 !tw-text-amber-700' : ''
                        }`}
                    >
                        <ClockIcon className="tw-h-3.5 tw-w-3.5 tw-shrink-0" aria-hidden="true" />
                        <span className="expenditure-toolbar__label">{$t('Pending changes ({{count}})', { count })}</span>
                    </button>,
                    toggleRoot,
                )}

            {legendRoot &&
                visible &&
                count > 0 &&
                ReactDOM.createPortal(
                    <p className="tw-mb-0 tw-mt-1 tw-flex tw-items-start tw-gap-x-2 tw-text-xs tw-text-charcoal-75">
                        <span className="bc-pending-chip tw-shrink-0 tw-cursor-default" data-label={$t('Pending')} />
                        <span>
                            {$t(
                                'Figures shown are the current approved value. A pending chip beside a figure means a budget change request is awaiting approval — click it for the detail. Pending amounts are not included in any total until approved.',
                            )}
                        </span>
                    </p>,
                    legendRoot,
                )}

            {visible &&
                count > 0 &&
                tileRoots.map((root, index) =>
                    ReactDOM.createPortal(
                        <button
                            type="button"
                            className="tw-mt-1 tw-flex tw-items-center tw-gap-x-1 tw-text-xs tw-font-medium tw-text-amber-700 hover:tw-underline"
                            onClick={(event) => setPopover({ anchor: event.currentTarget, list: items, selected: null })}
                        >
                            <ClockIcon className="tw-h-3 tw-w-3 tw-shrink-0" aria-hidden="true" />
                            {count === 1
                                ? $t('1 change pending · {{change}}', { change: formatChange(payload.net_change) })
                                : $t('{{count}} changes pending · {{change}}', { count, change: formatChange(payload.net_change) })}
                        </button>,
                        root,
                        index,
                    ),
                )}

            {popover && (
                <PendingPopover
                    popover={popover}
                    onSelect={(item) => setPopover({ ...popover, selected: item })}
                    onBack={() => setPopover({ ...popover, selected: null })}
                    onClose={() => setPopover(null)}
                    onApprove={startApproval}
                />
            )}

            {payload && (
                <ApprovalDetailModal
                    id="pending-line-change-approval"
                    modal={approvalModal}
                    row={approvalRow}
                    myViews={{ fetch_budget_change_url: payload.show_url, toggle_budget_change_url: payload.toggle_url }}
                    onClose={() => setApprovalRow(null)}
                    onActioned={afterAction}
                />
            )}
        </>
    );
}

function PendingPopover({ popover, onSelect, onBack, onClose, onApprove }) {
    const ref = useRef(null);
    const [position, setPosition] = useState(null);

    useLayoutEffect(() => {
        const rect = popover.anchor.getBoundingClientRect();
        const maxLeft = document.documentElement.clientWidth - POPOVER_WIDTH - 8;

        setPosition({
            top: rect.bottom + window.scrollY + 8,
            left: Math.max(8, Math.min(rect.right - POPOVER_WIDTH, maxLeft)) + window.scrollX,
        });
    }, [popover.anchor]);

    // Focus moves in on open and back to the chip on close, unless the user has
    // already put it somewhere else.
    useEffect(() => {
        const node = ref.current;

        if (!node) {
            return undefined;
        }

        node.focus();

        return () => {
            const active = document.activeElement;

            if ((!active || active === document.body || node.contains(active)) && popover.anchor.isConnected) {
                popover.anchor.focus();
            }
        };
    }, [position !== null, popover.anchor]);

    useEffect(() => {
        const onPointer = (event) => {
            if (!ref.current?.contains(event.target) && !popover.anchor.contains(event.target)) {
                onClose();
            }
        };
        const onKey = (event) => event.key === 'Escape' && onClose();

        document.addEventListener('mousedown', onPointer);
        document.addEventListener('keydown', onKey);
        window.addEventListener('resize', onClose);

        return () => {
            document.removeEventListener('mousedown', onPointer);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('resize', onClose);
        };
    }, [popover.anchor, onClose]);

    if (!position) {
        return null;
    }

    const { list, selected } = popover;

    return ReactDOM.createPortal(
        <div
            ref={ref}
            role="dialog"
            tabIndex={-1}
            aria-label={$t('Pending approval')}
            className="tw-absolute tw-z-[60] tw-rounded-xl tw-border tw-border-charcoal-15 tw-bg-white tw-px-[18px] tw-py-4 tw-text-sm tw-shadow-lg focus:tw-outline-none"
            style={{ top: position.top, left: position.left, width: POPOVER_WIDTH }}
        >
            <div className="tw-mb-2.5 tw-flex tw-items-center tw-justify-between">
                <div className="tw-flex tw-items-center tw-gap-x-1.5">
                    {selected && list && (
                        <button type="button" onClick={onBack} className="tw-text-charcoal-50 hover:tw-text-charcoal" aria-label={$t('Back')}>
                            <ChevronLeftIcon className="tw-h-3 tw-w-3" aria-hidden="true" />
                        </button>
                    )}
                    <span className="bc-pending-chip" data-label={selected ? $t('Pending approval') : $t('{{count}} pending', { count: list.length })} />
                </div>
                <button type="button" onClick={onClose} className="tw-text-charcoal-50 hover:tw-text-charcoal" aria-label={$t('Close')}>
                    <XMarkIcon className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />
                </button>
            </div>

            {selected ? (
                <PendingDetail item={selected} onApprove={onApprove} />
            ) : (
                <ul className="tw-m-0 tw-list-none tw-p-0">
                    {list.map((item) => (
                        <li key={item.request_id} className="tw-border-b tw-border-charcoal-10 last:tw-border-0">
                            <button
                                type="button"
                                onClick={() => onSelect(item)}
                                className="tw-flex tw-w-full tw-items-start tw-justify-between tw-gap-x-3 tw-py-2 tw-text-start hover:tw-bg-charcoal-10"
                            >
                                <span className="tw-min-w-0">
                                    <span className="tw-block tw-truncate tw-font-medium tw-text-charcoal">{item.line_name}</span>
                                    <span className="tw-block tw-text-xs tw-text-charcoal-75">
                                        {$t('Awaiting')}: {item.awaiting.length ? item.awaiting.join(', ') : DASH}
                                    </span>
                                </span>
                                <span className="tw-shrink-0 tw-font-medium tw-text-amber-700">{formatChange(item.change)}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>,
        document.body,
    );
}

function PendingDetail({ item, onApprove }) {
    const rows = [
        [$t('Line'), item.line_name],
        [$t('Current approved value'), decimal(item.current_value, 0)],
        [$t('Requested value'), decimal(item.requested_value, 0)],
        [$t('Change'), `${formatChange(item.change)} (${item.type_label})`, 'tw-text-amber-700'],
        [$t('Requested by'), `${item.requested_by} · ${localDate(item.requested_at)}`],
        [$t('Explanation'), item.explanation],
        [$t('Awaiting'), item.awaiting.length ? item.awaiting.join(', ') : DASH],
    ];

    return (
        <>
            <dl className="tw-m-0">
                {rows.map(([label, value, valueClass]) => (
                    <div key={label} className="tw-flex tw-justify-between tw-gap-x-4 tw-border-b tw-border-charcoal-10 tw-py-[5px]">
                        <dt className="tw-shrink-0 tw-font-normal tw-text-charcoal-75">{label}</dt>
                        <dd className={`tw-m-0 tw-text-end tw-font-medium ${valueClass ?? 'tw-text-charcoal'}`}>{value}</dd>
                    </div>
                ))}
            </dl>

            <div className="tw-mt-3 tw-flex tw-gap-2">
                {item.budget_changes_url && (
                    <Button as="a" href={item.budget_changes_url} variant="secondary" small className="tw-flex-1 tw-justify-center">
                        {$t('Open in Budget Changes')}
                    </Button>
                )}
                {item.can_action && (
                    <Button variant="primary" small className="tw-flex-1 tw-justify-center" onClick={() => onApprove(item)}>
                        {$t('Approve / Reject')}
                    </Button>
                )}
            </div>
        </>
    );
}

if (document.getElementById('PendingLineChanges')) {
    const rootEl = document.getElementById('PendingLineChanges');

    ReactDOM.render(
        <App>
            <PendingLineChanges endpoint={rootEl.dataset.endpoint} />
        </App>,
        rootEl,
    );
}
